import {
    AfterViewInit,
    Component,
    effect,
    inject,
    Signal,
    signal,
    viewChild,
    ViewContainerRef,
    WritableSignal,
} from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { Navbar } from './ui/navbar/navbar';
import { Sidebar } from './ui/sidebar/sidebar';
import { SideBarItem } from './ui/sidebar/models';
import { AppRoutes } from './app.routes';
import { AuthService } from './auth/services/auth.service';
import { LocalStorageService } from '../core/services/browser-storage/local-storage.service';
import { LocalStorageKeys } from '../core/services/browser-storage/types/local-storage.enum';
import { UserAppSettings } from '../core/services/browser-storage/types/user-app-settings.model';
import { NavbarPortalService } from '../core/services/navbar-portal/navbar-portal.service';
import { UserOnlineTrackingHubService } from './ui/user-online/infrastructure/hubs/user-online-tracking-hub.service';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { LangSwitcherComponent } from './ui/lang-switch/lang-switcher';

@Component({
    selector: 'app-root',
    templateUrl: './app.html',
    styleUrl: './app.scss',
    imports: [RouterModule, Navbar, Sidebar, LangSwitcherComponent],
})
export class App implements AfterViewInit {
    vcr: Signal<ViewContainerRef> = viewChild('vcr');

    public readonly authService: AuthService = inject(AuthService);
    private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
    private readonly router: Router = inject(Router);
    public readonly navbarService: NavbarPortalService = inject(NavbarPortalService);
    private readonly userOnlineTrackingHubService = inject(UserOnlineTrackingHubService);
    private title = inject(Title);
    private readonly document = inject(DOCUMENT);

    readonly sideBarItems: WritableSignal<SideBarItem[]> = signal<SideBarItem[]>([
        {
            name: $localize`:@@sidebar.items.auctions:Auctions`,
            icon: 'percent',
            route: AppRoutes.Auctions,
        },
        {
            name: $localize`:@@sidebar.items.companies:Companies`,
            icon: 'work_outline',
            route: AppRoutes.Organizations,
        },
    ]);

    readonly isAuthPage = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => this.isAuthRoute(event.urlAfterRedirects))
        ),
        { initialValue: this.isAuthRoute(this.document.location.pathname) }
    );

    constructor() {
        this.title.setTitle('Casas Finanzas Admin');

        this.localStorageService.setItem(LocalStorageKeys.UserAppSettings, new UserAppSettings());

        effect(() => {
            if (!this.authService.isAuthorized()) {
                this.router.navigate([AppRoutes.Login]);
            }
        });

        effect(() => {
            if (this.authService.isAuthorized()) {
                this.userOnlineTrackingHubService.connect();
            } else {
                this.userOnlineTrackingHubService.disconnect();
            }
        });
    }

    ngAfterViewInit(): void {
        this.navbarService.setViewContainerRef(this.vcr());
    }

    onSidebarItemClick(item: SideBarItem) {
        this.router.navigate([item.route]);
    }

    logout(): void {
        this.authService.logout();
    }

    private isAuthRoute(url: string): boolean {
        return url.includes(`/${AppRoutes.Login}`);
    }
}
